/**
 * ConversationHistoryDialog — browse past conversation sessions.
 * Lists saved sessions from /api/chat/sessions and lets the operator
 * switch to any of them from within the dialog.
 */
import { useQuery } from '@tanstack/react-query';
import { Clock, Inbox, MessageSquare, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@onestone/ui/dialog';
import { Button } from '@onestone/ui/button';
import { Skeleton } from '@onestone/ui/skeleton';
import { api } from '@/lib/api';
import { chatStore, type ConversationSummary } from '@/lib/chatStore';
import { cn } from '@onestone/ui/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
}

export function ConversationHistoryDialog({ open, onOpenChange, conversationId }: Props) {
  const query = useQuery({
    queryKey: ['chat', 'sessions', 'history-dialog'],
    queryFn: () => api<{ sessions: ConversationSummary[] }>('/api/chat/sessions'),
    enabled: open,
    refetchInterval: false,
  });

  const sessions = query.data?.sessions ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] min-h-[300px] w-full max-w-2xl flex-col gap-0 p-0">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
          <DialogTitle>Conversation History</DialogTitle>
          <DialogDescription>Past conversations — click one to load it into the chat panel.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-auto">
          {query.isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : query.isError ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <p className="text-sm text-destructive">{(query.error as Error).message}</p>
              <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
                <RefreshCw className="mr-1.5 h-3 w-3" />
                Retry
              </Button>
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <Inbox className="h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No saved conversations yet.</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-2 p-6">
              {sessions.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => {
                      void chatStore.switchConversation(s.id);
                      onOpenChange(false);
                    }}
                    className={cn(
                      'flex w-full flex-col gap-1 rounded-lg border bg-card p-3 text-left transition-all hover:-translate-y-px hover:border-primary/40 hover:shadow-sm',
                      s.id === conversationId ? 'border-primary/40 bg-primary/5' : 'border-border',
                    )}
                  >
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3 shrink-0" />
                      <span className="tabular-nums">{new Date(s.updatedAt).toLocaleString()}</span>
                      <span className="ml-auto flex items-center gap-1 font-mono">
                        <MessageSquare className="h-3 w-3" />
                        {s.messageCount}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {s.title || s.model || s.id.slice(0, 8)}
                      </span>
                      {s.id === conversationId && <span className="shrink-0 text-xs text-primary">active</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {s.provider} · {s.model}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
